import { FixedShape } from "@iventis/domain-model/model/fixedShape";
import GeoJSON from "geojson";
import { AnyFeature, MapObjectProperties } from "@iventis/map-types";
import { point, Position } from "@turf/helpers";
import { Listener, FixedShapeSupportedGeometry } from "../types/internal";
import { resizeRectangle } from "./geojson-helpers";
import { DrawingModifierCreators, ModifyableDrawingTool } from "./drawing-modifiers";
import { DrawingModifier } from "../types/store-schema";

export enum FixedShapeEvent {
    MOVE = "MOVE",
    START = "START",
    FINISH = "FINISH",
}

export type FixedShapeGeometry = GeoJSON.Feature<FixedShapeSupportedGeometry, MapObjectProperties>;

interface ListenerPattern {
    [FixedShapeEvent.MOVE]: ((feature: GeoJSON.Feature, moveEvent: { lng: number; lat: number }) => void)[];
    [FixedShapeEvent.START]: ((feature: GeoJSON.Feature) => void)[];
    [FixedShapeEvent.FINISH]: ((feature?: GeoJSON.Feature) => void)[];
}

export class CompositionFixedShape extends ModifyableDrawingTool {
    private listeners: ListenerPattern = {
        [FixedShapeEvent.MOVE]: [],
        [FixedShapeEvent.START]: [],
        [FixedShapeEvent.FINISH]: [],
    };

    private mouseMoveListener: Listener;

    private mouseClickListener: Listener;

    private mapRotateListener: Listener;

    private destroyed: boolean;

    private coordLastClick: { lng: number; lat: number } = { lng: NaN, lat: NaN };

    private coordLastHovered: { lng: number; lat: number } = { lng: NaN, lat: NaN };

    private idle = true;

    private mapBearing: number;

    private enterKeyListener: Listener;

    private rightClickListener: Listener;

    constructor(
        private object: AnyFeature,
        private operations: {
            onEnterKeyPress(callback: (e: KeyboardEvent) => void): Listener;
            onMouseMove: (callback: (e: { lng: number; lat: number }) => void, radius?: number) => Listener;
            onClick: (callback: (e: { lng: number; lat: number }) => void) => Listener;
            onRightClick: (callback: (e: { lng: number; lat: number }) => void) => Listener;
            onRotateEnd: (callback: (bearing: number) => void) => Listener;
        },
        getBearing: () => Promise<number>,
        drawingModifier: DrawingModifier,
        createDrawingModifier: DrawingModifierCreators
    ) {
        super(drawingModifier, createDrawingModifier);

        if (this.object.geometry.coordinates.length > 0) {
            this.idle = false;
        }
        (async () => {
            this.mapBearing = await getBearing();
        })();

        this.mapRotateListener = this.operations.onRotateEnd((bearing) => {
            this.mapBearing = bearing;
        });

        this.mouseMoveListener = this.operations.onMouseMove((mousePosition) => {
            if (this.destroyed) {
                // These events might come in after the class is destroyed. Do nothing if they do.
                return;
            }

            if (this.idle) {
                // Want to check if we are modifying where the first point is being placed, for example snapping to nearest coordinate.
                this.tryModify([point([mousePosition.lng, mousePosition.lat], { id: object.properties.id })]);
                // If idle there no objects to change the geometry of, so we can return early
                return;
            }

            // If we are modifying the drawing, apply the modification to the mouse position
            const [lng, lat] = this.tryModify([point([mousePosition.lng, mousePosition.lat], { id: object.properties.id })])[0].geometry.coordinates as Position;

            this.coordLastHovered = { lat, lng };
            this.listeners.MOVE.forEach((listener) => listener(this.getTransformation(lng, lat), { lng, lat }));
        });
        this.mouseClickListener = this.operations.onClick((position) => {
            if (this.destroyed) {
                return;
            }

            if (this.idle) {
                this.listeners.START.forEach((listener) => listener(this.finalTranslation(position.lng, position.lat)));
                this.coordLastClick = position;
                this.idle = false;
                return;
            }
            // If second click is in the same place, do not finish
            if (this.coordLastClick.lng === position.lng && this.coordLastClick.lat === position.lat) {
                return;
            }
            this.listeners.FINISH.forEach((listener) => listener(this.finalTranslation(position.lng, position.lat)));
        });

        this.enterKeyListener = this.operations.onEnterKeyPress((event) => {
            if (this.destroyed) {
                return;
            }
            if (event.key !== "Enter") return;

            // If last click is in the same place as currently hovering, do not perform append
            if (this.coordLastClick.lng === this.coordLastHovered.lng && this.coordLastClick.lat === this.coordLastHovered.lat) {
                this.listeners.FINISH.forEach((listener) => listener());
                return;
            }
            // otherwise update last coord, append a point and finish up
            this.coordLastClick = this.coordLastHovered;
            this.listeners.FINISH.forEach((listener) => listener(this.finalTranslation(this.coordLastHovered.lng, this.coordLastHovered.lat)));
        });

        this.rightClickListener = this.operations.onRightClick((position) => {
            if (this.destroyed) {
                return;
            }

            if (this.idle) {
                this.listeners.START.forEach((listener) => listener(this.finalTranslation(position.lng, position.lat)));
                this.coordLastClick = position;
                this.idle = false;
                return;
            }
            // If second click is in the same place, do not finish
            if (this.coordLastClick.lng === position.lng && this.coordLastClick.lat === position.lat) {
                return;
            }
            this.listeners.FINISH.forEach((listener) => listener(this.finalTranslation(position.lng, position.lat)));
        });
    }

    setObject(object: AnyFeature) {
        this.object = object;
    }

    getTransformation(lng: number, lat: number) {
        const transformedFeature: AnyFeature = {
            ...this.object,
            geometry: resizeShape[this.object.properties.fixedShape](this.object.geometry, [lng, lat], this.mapBearing),
        };
        return transformedFeature;
    }

    private finalTranslation(...[lng, lat]: Parameters<CompositionFixedShape["getTransformation"]>) {
        const latestTranslation = this.getLatestTranslation()?.[0];
        let coords: [number, number] = [lng, lat];
        if (latestTranslation?.geometry.type === "Point") {
            coords = [latestTranslation.geometry.coordinates[0], latestTranslation.geometry.coordinates[1]];
        }
        return this.getTransformation(...coords);
    }

    public on<E extends FixedShapeEvent>(event: E, callback: ListenerPattern[keyof ListenerPattern][0]) {
        this.listeners[event].push(callback as () => void);
        return this;
    }

    public destroy() {
        this.mouseMoveListener.remove();
        this.mouseClickListener.remove();
        this.mapRotateListener.remove();
        this.enterKeyListener.remove();
        this.rightClickListener.remove();
        this.idle = true;
        this.destroyed = true;
    }
}

/** Object with keys as the fixed shape type and values as the functions returning the new geometry */
const resizeShape: Record<FixedShape, (currentGeom: FixedShapeSupportedGeometry, newCoords: GeoJSON.Position, bearing: number) => FixedShapeSupportedGeometry> = {
    [FixedShape.Rectangle]: resizeRectangle,
};
